import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { TOTPSection, PasskeysSection } from "./SecurityPage";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

// Blocking gate shown instead of the dashboard after a first setup-link/invite
// login until the user enrolls TOTP, a passkey, or links Google — there is no
// password or email-OTP fallback in this product, so skipping this step is
// how accounts get permanently locked out. See
// .claude/specs/mandatory-2fa-enrollment.md.
export default function EnrollmentGatePage() {
  const { logout, refreshAuth } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");

  async function signInWithGoogle() {
    setGoogleLoading(true);
    setGoogleError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/google/url`);
      const d = await r.json();
      if (!r.ok) { setGoogleError(d.detail || "Google sign-in unavailable"); return; }
      window.location.href = d.auth_url;
    } catch {
      setGoogleError("Network error. Please try again.");
      setGoogleLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", justifyContent: "center", padding: "48px 20px" }}>
      <div style={{ maxWidth: 640, width: "100%" }}>
        <div style={{
          background: "white", borderRadius: 12, padding: "28px 32px", marginBottom: 24,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderLeft: `4px solid ${NAVY}`,
        }}>
          <h1 style={{ margin: 0, color: NAVY, fontSize: 20 }}>Secure your account to continue</h1>
          <p style={{ margin: "10px 0 0", color: "#475569", fontSize: 14, lineHeight: 1.6 }}>
            This account has no password — the only ways back in are an authenticator app, a passkey,
            or Google sign-in. Set up one of these now so you can never get locked out.
          </p>
        </div>

        <TOTPSection onSuccess={refreshAuth} />
        <div style={{ height: 20 }} />
        <PasskeysSection onSuccess={refreshAuth} />

        <div style={{
          background: "white", borderRadius: 12, padding: 28, marginTop: 20,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}>
          <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>Or sign in with Google</h3>
          <p style={{ margin: "4px 0 16px", fontSize: 13, color: "#64748b" }}>
            If this email has a Google account, linking it now also satisfies this step.
          </p>
          {googleError && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
              padding: "8px 14px", marginBottom: 12, fontSize: 13, color: "#dc2626",
            }}>
              {googleError}
            </div>
          )}
          <button
            onClick={signInWithGoogle}
            disabled={googleLoading}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10,
              border: "1.5px solid #e2e8f0", background: "white",
              fontSize: 14, fontWeight: 600, color: "#334155",
              cursor: googleLoading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {googleLoading ? "Redirecting…" : "Sign in with Google"}
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <button onClick={logout} style={{
            background: "none", border: "none", color: "#64748b", fontSize: 13,
            textDecoration: "underline", cursor: "pointer",
          }}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
